import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { useEffect, lazy, Suspense } from "react";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AnalyticsTracker } from "@/components/AnalyticsTracker";
import { GlossaryAutoScan } from "@/components/GlossaryAutoScan";
import { CookieConsent } from "@/components/CookieConsent";
import { CookieListDialog } from "@/components/CookieListDialog";
import Index from "./pages/Index";

// Lazy-load every non-home route so they don't bloat the initial bundle.
const NotFound = lazy(() => import("./pages/NotFound"));
const GSeriesPage = lazy(() => import("./pages/systems/GSeriesPage"));
const DSeriesPage = lazy(() => import("./pages/systems/DSeriesPage"));
const SynthiaPage = lazy(() => import("./pages/technology/SynthiaPage"));
const StoryPage = lazy(() => import("./pages/about/StoryPage"));
const TeamPage = lazy(() => import("./pages/about/TeamPage"));
const ESGPage = lazy(() => import("./pages/about/ESGPage"));
const CareersPage = lazy(() => import("./pages/about/CareersPage"));
const InvestorsPage = lazy(() => import("./pages/about/InvestorsPage"));
const InvestorAuthPage = lazy(() => import("./pages/about/InvestorAuthPage"));
const InvestorApprovalPage = lazy(() => import("./pages/about/InvestorApprovalPage"));
const CompanyPage = lazy(() => import("./pages/about/CompanyPage"));
const ArticlesPage = lazy(() => import("./pages/resources/ArticlesPage"));
const CaseStudiesPage = lazy(() => import("./pages/resources/CaseStudiesPage"));
const CaseStudyRoute = lazy(() => import("./pages/resources/case-studies/CaseStudyRoute"));
const CaseStudyAuthPage = lazy(() => import("./pages/resources/case-studies/CaseStudyAuthPage"));
const WhitePapersPage = lazy(() => import("./pages/resources/WhitePapersPage"));
const DownloadsPage = lazy(() => import("./pages/resources/DownloadsPage"));
const GlossaryPage = lazy(() => import("./pages/resources/GlossaryPage"));
const ResearchProgramsPage = lazy(() => import("./pages/resources/ResearchProgramsPage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const RequestLightPlanPage = lazy(() => import("./pages/RequestLightPlanPage"));
const UnsubscribePage = lazy(() => import("./pages/UnsubscribePage"));
const NewsletterUnsubscribePage = lazy(() => import("./pages/NewsletterUnsubscribePage"));
const ResetPasswordPage = lazy(() => import("./pages/ResetPasswordPage"));
const CookiePolicyPage = lazy(() => import("./pages/legal/CookiePolicyPage"));
const TermsPage = lazy(() => import("./pages/legal/TermsPage"));
const PrivacyPolicyPage = lazy(() => import("./pages/legal/PrivacyPolicyPage"));
const ChatInboxPage = lazy(() => import("./pages/admin/ChatInboxPage"));
const ApprovalsPage = lazy(() => import("./pages/admin/ApprovalsPage"));

const queryClient = new QueryClient();

const ScrollToTop = () => {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const id = hash.slice(1);
      // Wait for the target element to mount before scrolling
      requestAnimationFrame(() => {
        const el = document.getElementById(id);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
        } else {
          window.scrollTo(0, 0);
        }
      });
      return;
    }
    window.scrollTo(0, 0);
  }, [pathname, hash]);
  return null;
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <ScrollToTop />
        <AnalyticsTracker />
        <GlossaryAutoScan />
        <CookieConsent />
        <CookieListDialog />
        <Suspense fallback={<div className="min-h-screen bg-background" />}>
          <Routes>
          <Route path="/" element={<Index />} />
          <Route path="/systems/g-series" element={<GSeriesPage />} />
          <Route path="/systems/d-series" element={<DSeriesPage />} />
          <Route path="/software/synthia" element={<SynthiaPage />} />
          <Route path="/company/story" element={<StoryPage />} />
          <Route path="/people/team" element={<TeamPage />} />
          <Route path="/company/esg" element={<ESGPage />} />
          <Route path="/people/careers" element={<CareersPage />} />
          <Route path="/company/investors" element={<InvestorsPage />} />
          <Route path="/company/investors/auth" element={<InvestorAuthPage />} />
          <Route path="/company/investors/approval" element={<InvestorApprovalPage />} />
          <Route path="/company" element={<CompanyPage />} />
          <Route path="/resources/articles" element={<ArticlesPage />} />
          <Route path="/resources/downloads" element={<DownloadsPage />} />
          <Route path="/resources/glossary" element={<GlossaryPage />} />
          {/* Research section (new) */}
          <Route path="/research/programs" element={<ResearchProgramsPage />} />
          <Route path="/research/publications" element={<WhitePapersPage />} />
          <Route path="/research/case-studies" element={<CaseStudiesPage />} />
          <Route path="/research/case-studies/auth" element={<CaseStudyAuthPage />} />
          <Route path="/research/case-studies/ravnsborg" element={<CaseStudyRoute slug="ravnsborg" />} />
          <Route path="/research/case-studies/velocigro" element={<CaseStudyRoute slug="velocigro" />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/resources/light-plan" element={<RequestLightPlanPage />} />
          <Route path="/request-light-plan" element={<Navigate to="/resources/light-plan" replace />} />
          <Route path="/unsubscribe" element={<UnsubscribePage />} />
          <Route path="/newsletter/unsubscribe" element={<NewsletterUnsubscribePage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/legal/cookie-policy" element={<CookiePolicyPage />} />
          <Route path="/legal/terms" element={<TermsPage />} />
          <Route path="/legal/privacy" element={<PrivacyPolicyPage />} />
          <Route path="/admin/chat" element={<ChatInboxPage />} />
          <Route path="/admin/approvals" element={<ApprovalsPage />} />
          <Route path="/admin" element={<Navigate to="/admin/chat" replace />} />
          {/* Legacy URL redirects (from old site) */}
          <Route path="/about/team" element={<Navigate to="/people/team" replace />} />
          <Route path="/about/careers" element={<Navigate to="/people/careers" replace />} />
          <Route path="/about/story" element={<Navigate to="/company/story" replace />} />
          <Route path="/about/esg" element={<Navigate to="/company/esg" replace />} />
          <Route path="/about/investors" element={<Navigate to="/company/investors" replace />} />
          <Route path="/about/company" element={<Navigate to="/company" replace />} />
          <Route path="/products/g-series" element={<Navigate to="/systems/g-series" replace />} />
          <Route path="/products/d-series" element={<Navigate to="/systems/d-series" replace />} />
          <Route path="/synthia" element={<Navigate to="/software/synthia" replace />} />
          <Route path="/technology/synthia" element={<Navigate to="/software/synthia" replace />} />
          <Route path="/team" element={<Navigate to="/people/team" replace />} />
          <Route path="/our-story" element={<Navigate to="/company/story" replace />} />
          <Route path="/esg" element={<Navigate to="/company/esg" replace />} />
          <Route path="/our-solutions" element={<Navigate to="/systems/g-series" replace />} />
          <Route path="/our-solutions/lighting-solutions" element={<Navigate to="/systems/g-series" replace />} />
          <Route path="/tailored-lighting-solutions" element={<Navigate to="/systems/g-series" replace />} />
          <Route path="/farming-management-software" element={<Navigate to="/software/synthia" replace />} />
          <Route path="/rd-lab-for-plant-science" element={<Navigate to="/research/programs" replace />} />
          <Route path="/ai-and-machine-learning-in-vertical-farming" element={<Navigate to="/resources/articles" replace />} />
          <Route path="/coming-soon" element={<Navigate to="/" replace />} />
          <Route path="/technology" element={<Navigate to="/software/synthia" replace />} />
          <Route path="/partners" element={<Navigate to="/#partners" replace />} />
          <Route path="/latest-stories" element={<Navigate to="/resources/articles" replace />} />
          <Route path="/contact-us" element={<Navigate to="/contact" replace />} />
          <Route path="/get-started-free" element={<Navigate to="/contact" replace />} />
          <Route path="/tour" element={<Navigate to="/" replace />} />
          <Route path="/homeold" element={<Navigate to="/" replace />} />
          <Route path="/privacy-policy" element={<Navigate to="/legal/privacy" replace />} />
          <Route path="/cookies-policy" element={<Navigate to="/legal/cookie-policy" replace />} />
          <Route path="/terms-of-solution" element={<Navigate to="/legal/terms" replace />} />
          <Route path="/greentech-amsterdam-2024-photosynthetic-announces-partnership" element={<Navigate to="/resources/articles" replace />} />
          <Route path="/greentech-amsterdam-2024-photosynthetic-launches-new-dynamic-grow-light-solution-for-controlled-environment-agriculture" element={<Navigate to="/resources/articles" replace />} />
          <Route path="/photosynthetic-revealed-at-vertifarm-in-dortmund" element={<Navigate to="/resources/articles" replace />} />
          <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
